import { accountCategoryLabel, accountStatusLabel, formatAccountPeriod } from '../lib/accountPresentation'
import { useMemberTimeZone } from '../MemberTimeZoneContext'
import { useOptionalPrivanaI18n } from '@/i18n/PrivanaI18nProvider.jsx'
import AttributableCharges from '@/components/members/AttributableCharges.jsx';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ArrowLeft, CreditCard, ReceiptText, ShieldCheck, Sparkles } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useMemberPortalApi } from '../api/memberPortalApiClient'
import { formatDashboardCurrency } from '../lib/formatCurrency'

function formatDate(value, locale) {
  if (!value) return '—'
  try { return new Date(`${value}T00:00:00`).toLocaleDateString(locale, { month: 'short', day: 'numeric', year: 'numeric' }) } catch { return value }
}

export default function MemberAccountPage({ clubCode, summaryCurrency, heroImageUrl, heroImageFocus, accountResource, accountLoading, accountError, onAccountRefresh, isMobileApp = false }) {
  const { locale, t } = useOptionalPrivanaI18n()
  const timeZone = useMemberTimeZone()
  const navigate = useNavigate()
  const { memberPortalApiPost } = useMemberPortalApi()
  const statement = accountResource
  const loading = accountLoading
  const error = accountError
  const [payMessage, setPayMessage] = useState('')
  const [payLoading, setPayLoading] = useState(false)
  const [checkoutStarted, setCheckoutStarted] = useState(false)
  const pollTimerRef = useRef(null)
  const pollStartedAtRef = useRef(0)

  const stopCheckoutPolling = useCallback(() => {
    if (pollTimerRef.current) window.clearInterval(pollTimerRef.current)
    pollTimerRef.current = null
    pollStartedAtRef.current = 0
  }, [])

  const startCheckoutPolling = useCallback(() => {
    stopCheckoutPolling()
    pollStartedAtRef.current = Date.now()
    pollTimerRef.current = window.setInterval(async () => {
      if (Date.now() - pollStartedAtRef.current > 60000) {
        stopCheckoutPolling()
        return
      }
      try {
        const nextStatement = await onAccountRefresh({ reason: 'payment_poll', force: true })
        const refreshedTotal = Number(nextStatement?.totalAmount)
        if (Number.isFinite(refreshedTotal) && refreshedTotal <= 0) {
          setPayMessage('paymentReceived')
          setCheckoutStarted(false)
          stopCheckoutPolling()
        }
      } catch {
        // Keep polling briefly; transient network errors should not mark the balance paid.
      }
    }, 3000)
  }, [onAccountRefresh, stopCheckoutPolling])

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const payment = params.get('payment')
    if (payment === 'success') { setPayMessage('statementUpdating'); setCheckoutStarted(true); startCheckoutPolling() }
    if (payment === 'cancelled') setPayMessage('paymentCancelled')
    return stopCheckoutPolling
  }, [startCheckoutPolling, stopCheckoutPolling])

  const handleRetry = useCallback(() => onAccountRefresh({ reason: 'retry', force: true }).catch(() => null), [onAccountRefresh])

  const currency = statement?.currency || summaryCurrency || { symbol: '$', code: 'USD' }
  const rawTotal = statement?.totalAmount
  const hasValidBalance = rawTotal !== null && rawTotal !== undefined && rawTotal !== '' && Number.isFinite(Number(rawTotal))
  const total = hasValidBalance ? Number(statement.totalAmount) : null
  const itemCount = Number.isFinite(Number(statement?.itemCount)) ? Number(statement.itemCount) : null
  const categories = statement?.categories || []
  const items = statement?.items || []
  const amountLabel = useMemo(() => hasValidBalance ? formatDashboardCurrency(total, { ...currency, locale }) : null, [hasValidBalance, total, currency, locale])
  const payStatusMessage = statement?.payment?.enabled === false
    ? 'paymentsDisabled'
    : statement?.payment && !statement?.payment?.onboardingComplete
      ? 'paymentsSetup'
      : ''
  const heroFocus = useMemo(() => ({
    x: Number.isFinite(Number(heroImageFocus?.x ?? heroImageFocus?.hero_image_focus_x)) ? Number(heroImageFocus?.x ?? heroImageFocus?.hero_image_focus_x) : 50,
    y: Number.isFinite(Number(heroImageFocus?.y ?? heroImageFocus?.hero_image_focus_y)) ? Number(heroImageFocus?.y ?? heroImageFocus?.hero_image_focus_y) : 50,
  }), [heroImageFocus])

  async function handlePayCurrentBalance() {
    const checkoutWindow = window.open(`/privana-pay/checkout-opening?lang=${encodeURIComponent(locale)}`, '_blank')
    if (!checkoutWindow) { setPayMessage('allowPopups'); return }
    setPayMessage('preparingCheckout')
    setPayLoading(true)
    try {
      const res = await memberPortalApiPost(`/api/members-portal/${clubCode}/account/pay-current-balance`, {})
      const body = await res.json().catch(() => ({}))
      if (body?.url) {
        checkoutWindow.location.assign(body.url)
        setCheckoutStarted(true)
        setPayMessage('checkoutOpened')
        startCheckoutPolling()
        return
      }
      const fallbackMessage = body?.error === 'privana_pay_disabled' ? 'paymentsDisabled' : body?.error === 'payments_not_ready' ? 'paymentsSetup' : body?.no_balance_due ? 'noBalanceDue' : body?.error === 'balance_unavailable' ? 'balanceUnconfirmed' : body?.error === 'currency_unresolved' ? 'currencyUnavailable' : 'checkoutFailed'
      console.warn('[privana-pay] checkout session response missing url', { error: body?.error || null, message: body?.message || null })
      try { checkoutWindow.location.assign(`/privana-pay/payment-cancelled?lang=${encodeURIComponent(locale)}`) } catch { try { checkoutWindow.close() } catch {} }
      setPayMessage(fallbackMessage)
    } catch (error) {
      console.warn('[privana-pay] checkout session request failed', error)
      try { checkoutWindow.location.assign(`/privana-pay/payment-cancelled?lang=${encodeURIComponent(locale)}`) } catch { try { checkoutWindow.close() } catch {} }
      setPayMessage('checkoutFailed')
    }
    finally { setPayLoading(false) }
  }

  return (
    <div className="mp-account-page">
      {!isMobileApp ? <button type="button" className="mp-link mp-account-back" onClick={() => navigate(`/${clubCode}/dashboard`)}><ArrowLeft size={16} /> {t('memberAccount.dashboard')}</button> : null}
      <section className={`mp-account-hero ${heroImageUrl ? 'mp-account-hero--with-image' : 'mp-account-hero--placeholder'}`}>
        <div className="mp-account-hero__imageWrap" aria-hidden="true">
          {heroImageUrl ? (
            <img
              className="mp-account-hero__image"
              src={heroImageUrl}
              alt=""
              loading="eager"
              decoding="async"
              style={{ objectPosition: `${heroFocus.x}% ${heroFocus.y}%` }}
            />
          ) : (
            <div className="mp-account-hero__imagePlaceholder" />
          )}
        </div>
        <div className="mp-account-hero__summary">
          <div className="mp-account-kicker"><Sparkles size={16} /> {t('memberAccount.account')}</div>
          <h1>{t('memberAccount.currentBalance')}</h1>
          <div className="mp-account-amount" aria-busy={loading && !hasValidBalance}>{hasValidBalance ? amountLabel : error ? t('memberAccount.balanceUnavailable') : t('memberAccount.balanceLoading')}</div>
          {statement?.stale && hasValidBalance ? <small>{t('memberAccount.lastUpdated')} {new Date(statement.updatedAt).toLocaleTimeString(locale, { timeZone, hour: 'numeric', minute: '2-digit' })}{loading ? t('memberAccount.refreshing') : ''}</small> : null}
          <p>{t('memberAccount.monthlyBillHelp')}</p>
        </div>
        <div className="mp-account-hero__meta">
          <div><span>{t('memberAccount.billingPeriod')}</span><strong>{formatAccountPeriod(statement?.period, locale) || t('memberAccount.currentMonth')}</strong></div>
          <div><span>{t('memberAccount.billableItems')}</span><strong>{itemCount == null ? '—' : itemCount.toLocaleString(locale)}</strong></div>
        </div>
      </section>

      <section className="mp-account-pay-card">
        <div><h2>{t('memberAccount.payBalance')}</h2><p>{t('memberAccount.settleHelp')}</p>{payStatusMessage ? <p className="mp-account-pay-card__message">{t(`memberAccount.${payStatusMessage}`)}</p> : null}{payMessage ? <p className="mp-account-pay-card__message">{t(`memberAccount.${payMessage}`)}</p> : null}{checkoutStarted ? <p className="mp-account-pay-card__message mp-account-pay-card__message--subtle">{t('memberAccount.autoRefreshHelp')}</p> : null}</div>
        <button type="button" className="mp-primary" disabled={payLoading || !hasValidBalance || loading || total <= 0 || Boolean(payStatusMessage) || Boolean(error)} onClick={handlePayCurrentBalance}><CreditCard size={18} /> {payLoading ? t('memberAccount.preparingCheckout') : t('memberAccount.payBalance')}</button>
      </section>

      {error ? <div className="mp-empty mp-empty--error">{t('memberAccount.loadFailed')} <button type="button" className="mp-link" disabled={loading} onClick={handleRetry}>{t('memberAccount.retry')}</button></div> : null}
      {!loading && !error && hasValidBalance && total === 0 && itemCount === 0 ? <div className="mp-account-empty"><ShieldCheck size={24} /><h2>{t('memberAccount.noCharges')}</h2><p>{t('memberAccount.clearHelp')}</p></div> : null}

      <AttributableCharges currency={currency} charges={statement?.attributable_charges || []} />
      {itemCount > 0 ? <section className="mp-account-categories">{categories.map((cat) => <article className="mp-account-category" key={cat.category}><span>{accountCategoryLabel(cat, items, t)}</span><strong>{formatDashboardCurrency(cat.total_amount, { ...currency, locale })}</strong><small>{t(cat.item_count === 1 ? 'memberAccount.items' : 'memberAccount.itemsMany', { count: Number(cat.item_count).toLocaleString(locale) })}</small></article>)}</section> : null}

      {itemCount > 0 ? <section className="mp-panel-card mp-account-items"><div className="mp-panel-header"><div><div className="mp-panel-title">{t('memberAccount.statementItems')}</div><div className="mp-panel-subtitle">{t('memberAccount.includedThisMonth')}</div></div></div><div className="mp-panel-body">{items.map((item) => <div className="mp-account-item" key={item.id}><div className="mp-account-item__icon"><ReceiptText size={18} /></div><div><strong>{item.description}</strong><span>{formatDate(item.date, locale)} · {accountCategoryLabel(item, items, t)}</span></div><div className="mp-account-item__amount"><strong>{formatDashboardCurrency(item.amount, { ...currency, locale })}</strong><span>{accountStatusLabel(item.status || 'open', t)}</span></div></div>)}</div></section> : null}
    </div>
  )
}
